import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { authLoadingAtom, authUserAtom, getUserById } from '@consentropy/shared';
import {
  createProject,
  deleteProject,
  getProjectById,
  getProjectsByClient,
  saveUpdatedProject,
  updateProject,
} from '../services/projectService';
import type { CreateProjectInput, Project, UpdateProjectInput } from '@consentropy/shared';
import { removeProjectContextFromKnowledge, safeKnowledgeIngest } from '../services/knowledgeIngestion';
import { scheduleProjectKnowledgeSync } from './syncProjectKnowledge';

const loggedProjectCompanyIds = new Set<string>();
type CreateProjectMutationInput = Omit<CreateProjectInput, 'companyId' | 'clientId'> & {
  companyId?: string;
};

function userCanAccessProject(project: Project, userCompanyId?: string): boolean {
  return !userCompanyId || project.companyId === userCompanyId;
}

async function getDynamoUserCompanyId(userId?: string, fallbackUserId?: string): Promise<string | undefined> {
  if (!userId) return undefined;
  let user;

  try {
    user = await getUserById(userId);
  } catch (error) {
    if (!fallbackUserId || fallbackUserId === userId) {
      throw error;
    }

    user = await getUserById(fallbackUserId);
  }

  const companyId = user.companyId;

  if (import.meta.env.DEV && companyId && !loggedProjectCompanyIds.has(companyId)) {
    console.info('[projects] Dynamo user companyId used as project company id:', companyId);
    loggedProjectCompanyIds.add(companyId);
  }

  return companyId;
}

async function requireProjectCompanyId(
  userId?: string,
  fallbackCompanyId?: string,
  fallbackUserId?: string,
): Promise<string> {
  const companyId = userId ? await getDynamoUserCompanyId(userId, fallbackUserId) : fallbackCompanyId;

  if (!companyId) {
    throw new Error('Project company id is missing.');
  }

  return companyId;
}

function mergeUpdatedProject(
  existing: Project | undefined,
  updated: Project,
  input: UpdateProjectInput,
): Project {
  return {
    ...existing,
    ...updated,
    ...input,
    id: existing?.id ?? updated.id,
  };
}

export function useProjects(companyId?: string) {
  const user = useAtomValue(authUserAtom);
  const isAuthLoading = useAtomValue(authLoadingAtom);

  return useQuery({
    queryKey: user?.id ? ['projects', 'current-user', user.id] : ['projects', companyId],
    queryFn: async () => {
      // Prefer the companyId already present on the authenticated user (decoded from
      // the JWT at login) to avoid an extra getUserById round-trip on load. Fall back
      // to the DynamoDB lookup only when the token carries no companyId claim.
      const effectiveCompanyId =
        user?.companyId ?? (await requireProjectCompanyId(user?.id, companyId, user?.username));
      return getProjectsByClient(effectiveCompanyId);
    },
    enabled: !isAuthLoading && (!!user?.id || !!companyId),
  });
}

export function useProject(id: string) {
  const user = useAtomValue(authUserAtom);
  const isAuthLoading = useAtomValue(authLoadingAtom);

  return useQuery({
    queryKey: ['project', id],
    queryFn: async () => {
      const userCompanyId = await requireProjectCompanyId(user?.id, undefined, user?.username);
      const project = await getProjectById(id, userCompanyId);
      if (!userCanAccessProject(project, userCompanyId)) {
        throw new Error('Project not found or access denied.');
      }
      return project;
    },
    enabled: !!id && !isAuthLoading && !!user?.id,
  });
}

export function useCreateProject() {
  const queryClient = useQueryClient();
  const user = useAtomValue(authUserAtom);

  return useMutation({
    mutationFn: async (input: CreateProjectMutationInput) => {
      const userCompanyId = await requireProjectCompanyId(user?.id, input.companyId, user?.username);
      return createProject({
        ...input,
        companyId: userCompanyId,
      });
    },
    onSuccess: (project) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void queryClient.invalidateQueries({ queryKey: ['projects', project.companyId] });
    },
  });
}

export function useUpdateProject() {
  const queryClient = useQueryClient();
  const user = useAtomValue(authUserAtom);

  return useMutation({
    mutationFn: async ({ id, input }: { id: string; input: UpdateProjectInput }) => {
      const userCompanyId = await requireProjectCompanyId(user?.id, input.companyId, user?.username);
      return updateProject(id, {
        ...input,
        companyId: userCompanyId,
      });
    },
    onMutate: async ({ id, input }) => {
      await queryClient.cancelQueries({ queryKey: ['project', id] });
      await queryClient.cancelQueries({ queryKey: ['projects'] });

      const previousProject = queryClient.getQueryData<Project>(['project', id]);
      const previousProjects = queryClient.getQueriesData<Project[]>({ queryKey: ['projects'] });
      const previousProjectFromList = previousProjects
        .flatMap(([, projects]) => projects ?? [])
        .find((project) => project.id === id);
      const optimisticProject = previousProject ?? previousProjectFromList;

      if (previousProject) {
        queryClient.setQueryData<Project>(['project', id], {
          ...previousProject,
          ...input,
        });
      }

      if (optimisticProject) {
        saveUpdatedProject({
          ...optimisticProject,
          ...input,
          id,
        });
      }

      queryClient.setQueriesData<Project[]>({ queryKey: ['projects'] }, (current) => {
        if (!current) return current;

        return current.map((project) =>
          project.id === id
            ? {
                ...project,
                ...input,
              }
            : project,
        );
      });

      return { previousProject, previousProjects, optimisticProject };
    },
    onError: (_error, { id }, context) => {
      if (context?.previousProject) {
        queryClient.setQueryData(['project', id], context.previousProject);
      }

      if (context?.optimisticProject) {
        saveUpdatedProject(context.optimisticProject);
      }

      context?.previousProjects.forEach(([queryKey, data]) => {
        queryClient.setQueryData(queryKey, data);
      });
    },
    onSuccess: (updatedProject, { id, input }) => {
      const mergedProject = mergeUpdatedProject(
        queryClient.getQueryData<Project>(['project', id]),
        updatedProject,
        input,
      );

      queryClient.setQueryData<Project>(['project', id], mergedProject);
      saveUpdatedProject(mergedProject);
      queryClient.setQueriesData<Project[]>({ queryKey: ['projects'] }, (current) => {
        if (!current) return current;

        return current.map((project) =>
          project.id === id ? mergeUpdatedProject(project, updatedProject, input) : project,
        );
      });
      void queryClient.invalidateQueries({ queryKey: ['projects'], refetchType: 'inactive' });
      void queryClient.invalidateQueries({ queryKey: ['project', id], refetchType: 'inactive' });
      scheduleProjectKnowledgeSync(queryClient, id);
    },
  });
}

export function useDeleteProject() {
  const queryClient = useQueryClient();
  const user = useAtomValue(authUserAtom);

  return useMutation({
    mutationFn: async (id: string) => {
      const userCompanyId = await requireProjectCompanyId(user?.id, undefined, user?.username);
      return deleteProject(id, userCompanyId);
    },
    onSuccess: (_data, id) => {
      void queryClient.invalidateQueries({ queryKey: ['projects'] });
      void safeKnowledgeIngest(() => removeProjectContextFromKnowledge(id));
    },
  });
}
